/**
 * useAuth.js — Auth state management for Beat Arranger
 * 
 * Three states:
 * 1. NO TOKEN — unauthenticated direct visit
 * 2. GUEST TOKEN — workshop flow (key: beat_arranger_guest_token)
 * 3. REAL USER JWT — authenticated user (key: ltbb-ba-token)
 */

import { useState, useCallback, useEffect } from 'react';

const TOKEN_KEY = 'ltbb-ba-token';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useAuth() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Fetch user profile on mount if token exists
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    fetch(`${BACKEND_URL}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        // Token expired or invalid
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setIsAuthenticated(false);
        return null;
      })
      .then((data) => {
        if (data) {
          setUser(data);
          setIsAuthenticated(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(({ token: newToken, user: newUser }) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('beat_arranger_project_id');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  return { token, user, isAuthenticated, loading, login, logout, getToken };
}

export default useAuth;
